import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface DeletedRecipeNotFoundProps {
  recipeId: number;
}

type FireParticle = {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  life: number;
  decay: number;
  size: number;
  wobble: number;
};

const DeletedRecipeNotFound: React.FC<DeletedRecipeNotFoundProps> = ({ recipeId }) => {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const surgeUntilRef = useRef(0);
  const [burning, setBurning] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    const particles: FireParticle[] = [];
    let animationFrame = 0;
    let width = 0;
    let height = 0;

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      const scale = Math.min(window.devicePixelRatio || 1, 2);
      width = bounds.width;
      height = bounds.height;
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      context.setTransform(scale, 0, 0, scale, 0, 0);
    };

    const spawn = (now: number) => {
      const surging = now < surgeUntilRef.current;
      const count = surging ? 6 : 3;
      for (let index = 0; index < count; index += 1) {
        particles.push({
          x: width / 2 + (Math.random() + Math.random() - 1) * width * 0.28,
          y: height - 43 + Math.random() * 15,
          velocityX: (Math.random() - 0.5) * (surging ? 1.8 : 1.1),
          velocityY: -(1.6 + Math.random() * (surging ? 3.4 : 2.2)),
          life: 1,
          decay: 0.012 + Math.random() * 0.016,
          size: 8 + Math.random() * (surging ? 24 : 17),
          wobble: Math.random() * 8,
        });
      }
    };

    const draw = (now: number) => {
      context.clearRect(0, 0, width, height);
      spawn(now);
      context.globalCompositeOperation = 'lighter';

      for (let index = particles.length - 1; index >= 0; index -= 1) {
        const particle = particles[index];
        particle.x += particle.velocityX + Math.sin(now / 170 + particle.wobble) * 0.55;
        particle.y += particle.velocityY;
        particle.life -= particle.decay;
        particle.size *= 0.994;
        if (particle.life <= 0 || particle.y < 4) {
          particles.splice(index, 1);
          continue;
        }

        const glow = context.createRadialGradient(
          particle.x,
          particle.y,
          0,
          particle.x,
          particle.y,
          particle.size
        );
        glow.addColorStop(0, `rgba(255,242,150,${particle.life * 0.92})`);
        glow.addColorStop(0.27, `rgba(255,145,20,${particle.life * 0.82})`);
        glow.addColorStop(0.68, `rgba(238,55,8,${particle.life * 0.44})`);
        glow.addColorStop(1, 'rgba(116,20,4,0)');
        context.fillStyle = glow;
        context.beginPath();
        context.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        context.fill();
      }

      context.globalCompositeOperation = 'source-over';
      animationFrame = window.requestAnimationFrame(draw);
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    resize();
    animationFrame = window.requestAnimationFrame(draw);

    return () => {
      resizeObserver.disconnect();
      window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  const replayBurn = () => {
    surgeUntilRef.current = performance.now() + 1600;
    setBurning(false);
    window.requestAnimationFrame(() => setBurning(true));
    window.setTimeout(() => setBurning(false), 1900);
  };

  return (
    <main className="deleted-recipe-404" aria-labelledby="deleted-recipe-title">
      <header className="deleted-recipe-404__nav">
        <button type="button" className="deleted-recipe-404__brand" onClick={() => navigate('/')} aria-label="Go to recipes">
          <span className="deleted-recipe-404__brand-mark" aria-hidden="true">K</span>
          <span>Kitchen</span>
        </button>
        <button type="button" className="deleted-recipe-404__back" onClick={() => navigate('/')}>
          ← All recipes
        </button>
      </header>

      <section className="deleted-recipe-404__stage">
        <p className="deleted-recipe-404__code">ERROR 404</p>
        <h1 id="deleted-recipe-title">This recipe is toast.</h1>
        <p className="deleted-recipe-404__message">It was deleted and can’t be opened from its old address.</p>

        <div className={`deleted-recipe-404__fire ${burning ? 'deleted-recipe-404__fire--surging' : ''}`}>
          <canvas ref={canvasRef} aria-hidden="true" />
          <div className="deleted-recipe-404__ash" aria-hidden="true" />
          <div className="deleted-recipe-404__identifier">recipe #{recipeId}</div>
        </div>

        <div className="deleted-recipe-404__actions">
          <button type="button" className="deleted-recipe-404__primary" onClick={() => navigate('/')}>
            Back to recipes
          </button>
          <button type="button" className="deleted-recipe-404__replay" onClick={replayBurn}>
            Replay burn
          </button>
        </div>
      </section>
    </main>
  );
};

export default DeletedRecipeNotFound;
